import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStoreValue } from 'store';
import { Text, RadioTile, Checkbox, Link, Stack, Icon, useBreakpoint } from 'basis';
import { LayoutPage, Form, LayoutContent, Nudge } from 'components';
import { Tooltip, ChakraProvider } from '@chakra-ui/react';
import { STEP_COMMITMENTS } from '_config';
import { DEV_FEATURES } from '__dev/devFeatures';
import { institutions as defaultInstitutions } from '_config/defaultFormValues';
import { BalanceTransferList } from 'balance-transfer-ui';
import { isAU, submitHandler, getProductInfo } from 'utils';
import { useEventTracking } from 'react-event-tracker';
import { getDataLayerElements } from 'utils/getDataLayerElements';
import {
  useFormInitialValues,
  useErrorTrackingAndUpdateStore,
  useQueryBalanceTransferInstitutions,
  useSteps,
  useDataLayerAndAnalyticsLinks,
} from '../../hooks';
import { calculateTotalBalanceTransfer } from '../credit-limit-request/utils';
import { AmountAlertModal, DebtAlertModal } from './AlertModal';

const FORM_ID = 'balanceTransfer';
const maxBTAmountFactor = 0.9;
const minBTAmount = 500;

export const BalanceTransfer = () => {
  const history = useHistory();
  const [storeState, updateStore] = useStoreValue();
  const { acquisition } = storeState;
  const { trackEvent } = useEventTracking();
  const initialValues = useFormInitialValues(FORM_ID);
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Next');
  const { nextStep } = useSteps();
  const [institutions, setInstitutions] = useState([]);
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);

  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isDebtAlertModalOpen, setIsDebtAlertModalOpen] = useState(false);
  const onBTInstitutionsSuccess = useCallback(institutionList => {
    setInstitutions(institutionList?.map(institution => ({ name: institution.name, code: institution.id })) ?? []);
    updateStore({
      acquisition: {
        ...acquisition,
        balanceTransferInstitutions: { institutions: institutionList },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { queryBTInstitutions } = useQueryBalanceTransferInstitutions({
    onError: handleErrorCallback,
    onSuccess: onBTInstitutionsSuccess,
    fallbackValue: storeState.acquisition?.balanceTransferInstitutions?.institutions || [],
  });
  const submitButtonLabel = 'Next';
  const offerDescription = storeState.offer?.description;
  const maxBalanceTransferAmount = getProductInfo().maxCreditLimit * maxBTAmountFactor;

  useEffect(() => {
    if (DEV_FEATURES.OTP_SKIP) {
      onBTInstitutionsSuccess(defaultInstitutions);
      return;
    }
    if (storeState.acquisition?.balanceTransferInstitutions?.institutions) {
      onBTInstitutionsSuccess(storeState.acquisition?.balanceTransferInstitutions?.institutions);
      return;
    }
    queryBTInstitutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToNextPage = useCallback(
    formValues => {
      trackEvent({
        event: {
          category: 'application',
          action: 'application-navigation',
          location: 'balance-transfer',
          label: submitButtonLabel,
        },
        ...getDataLayerElements({
          ...storeState,
          acquisition: {
            ...storeState.acquisition,
            balanceTransfer: { requestBalanceTransfer: formValues.requestBalanceTransfer },
          },
        }),
      });

      updateStore({
        activeStep: nextStep,
        applicationSubmitted: false,
      });
      history.push(nextStep);
    },
    [trackEvent, storeState, updateStore, nextStep, history],
  );

  const onSubmit = useCallback(
    ({ values, errors }) => {
      if (Object.keys(errors).length > 0) {
        return;
      }

      if (
        values.requestBalanceTransfer === 'yes' &&
        calculateTotalBalanceTransfer(values.balanceTransferList) > maxBalanceTransferAmount
      ) {
        setIsAlertModalOpen(true);
        return;
      }

      const balanceTransferList = Object.fromEntries(
        Object.entries(values.balanceTransferList ?? {}).map(([key, item]) => [
          key,
          {
            ...item,
            amount: parseFloat(item.amount) || '',
            ...(item.institutionName === 'Other Financial Institution' && {
              institutionName: item.bankName,
            }),
            bankName: undefined,
          },
        ]),
      );

      const newAcquisition = {
        ...acquisition,
        balanceTransfer: { ...values, balanceTransferList },
      };
      updateStore({
        acquisition: newAcquisition,
      });

      if (
        values.requestBalanceTransfer === 'yes' &&
        (storeState.acquisition?.additionalDebtsDetails?.hasOtherDebts === 'no' ||
          (storeState.acquisition?.additionalDebtsDetails?.hasOtherDebts === 'yes' &&
            (storeState.acquisition?.additionalDebtsDetails?.amountOwingOnCreditCard === '0' ||
              storeState.acquisition?.additionalDebtsDetails?.totalCardLimits === '0')))
      ) {
        setIsDebtAlertModalOpen(true);
        return;
      }

      submitHandler({
        submit: () => {
          goToNextPage(values);
        },
        errors,
      });
    },
    [acquisition, goToNextPage, updateStore, maxBalanceTransferAmount, storeState],
  );

  return (
    <LayoutPage>
      <Form
        id={FORM_ID}
        initialValues={initialValues}
        onSubmit={onSubmit}
        saveDraftOnSubmit={false}
        saveDraftOnSessionTimeout
      >
        {formData => {
          return (
            <LayoutContent rowsGap="6">
              <Nudge title={offerDescription} />
              <RadioTile
                name="requestBalanceTransfer"
                label="Do you have a non-Latitude credit card balance you’d like to transfer?"
                options={[
                  {
                    label: 'Yes',
                    value: 'yes',
                  },
                  {
                    label: 'No',
                    value: 'no',
                  },
                ]}
                showCircles
                columns={1}
                color="white"
                testId="request-balance-transfer-radio-group"
              />

              {formData.state.values.requestBalanceTransfer === 'yes' && (
                <Stack gap="4">
                  <BalanceTransferList
                    name="balanceTransferList"
                    countryCode={isAU() ? 'AU' : 'NZ'}
                    formData={formData}
                    setFormValues={formData.setValues}
                    testId="balance-transfer-list"
                    institutionList={institutions}
                    helpText={{
                      billerRef: 'Find it on your statement or in online banking.',
                    }}
                    billerRefHelpText={<BillerRefTooltip />}
                    onClickRemoveCard={index => {
                      trackEventSender({
                        category: 'application',
                        action: 'application-navigation',
                        label: 'remove transfer',
                        location: 'balance-transfer',
                      });
                    }}
                    onClickAddCard={() => {
                      trackEventSender({
                        category: 'application',
                        action: 'application-navigation',
                        label: 'Add another credit card',
                        location: 'balance-transfer',
                      });
                    }}
                    validator={{
                      amountValidate: {
                        pattern: {
                          validPattern: '^[0-9]+(\\.[0-9]{1,2})?$',
                          maxValue: maxBalanceTransferAmount,
                          minValue: minBTAmount,
                        },
                        messages: {
                          required: 'Please enter the amount to transfer.',
                          invalid: 'Please enter a valid amount to transfer.',
                          maxValue: `The maximum amount is $${maxBalanceTransferAmount?.toLocaleString()}.`,
                          minValue: `The minimum amount is $${minBTAmount}.`,
                        },
                      },
                    }}
                  />
                  <Text textStyle="body2">
                    Balance transfers can take up to 10 business days to process. If we're unable to complete your
                    transfer, we'll let you know.
                  </Text>
                  <Checkbox
                    name="tncConsent"
                    testId="tnc-consent"
                    label=""
                    validate={value => (value === false ? 'You must agree before you continue' : null)}
                  >
                    <Text textStyle="body2">
                      I have read and agree to the{' '}
                      <Link
                        href="https://assets.latitudefinancial.com/legals/terms-conditions/balance-transfer-termsconds.pdf"
                        newTab
                        testId="tnc-consent-link"
                        onClick={() => {
                          trackEventSender({
                            label: 'Terms and Conditions',
                            location: 'balance-transfer',
                          });
                        }}
                      >
                        Terms and Conditions
                      </Link>{' '}
                      of the balance transfer.
                    </Text>
                  </Checkbox>
                  <Checkbox
                    name="confirmationConsent"
                    testId="confirmation-consent"
                    label=""
                    validate={value => (value === false ? 'You must agree before you continue' : null)}
                  >
                    <Text textStyle="body2">
                      I acknowledge and confirm that the account being transferred is in my name, and does not belong to
                      another account holder.
                    </Text>
                  </Checkbox>
                </Stack>
              )}
            </LayoutContent>
          );
        }}
      </Form>
      <AmountAlertModal
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        maximumAmount={maxBalanceTransferAmount}
      />
      <DebtAlertModal
        isOpen={isDebtAlertModalOpen}
        onClose={() => setIsDebtAlertModalOpen(false)}
        onClick={() => {
          setIsDebtAlertModalOpen(false);
          updateStore({ activeStep: STEP_COMMITMENTS });
          history.push(STEP_COMMITMENTS);
        }}
      />
    </LayoutPage>
  );
};

const BillerRefTooltip = () => {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  const [isOpen, setIsOpen] = useState(false);
  const clickHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  return (
    <ChakraProvider>
      <Tooltip
        placement="top"
        hasArrow
        width={343}
        color="white"
        fontSize="md"
        textAlign="center"
        bg="#535862"
        borderRadius="8px"
        padding="8px 12px"
        openDelay={50}
        {...(isMobile ? { isOpen } : {})}
        label="Your credit card number should match your BPAY reference number. This can be found on your credit card statement from your financial institution. Additional cardholder numbers are not accepted."
      >
        <span
          role="button"
          tabIndex={0}
          onClick={isMobile ? clickHandler : undefined}
          onKeyDown={isMobile ? clickHandler : undefined}
          onBlur={isMobile ? () => setIsOpen(false) : undefined}
        >
          <Icon name="question" color="grey.t75" />
        </span>
      </Tooltip>
    </ChakraProvider>
  );
};
